import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDateFnsLocaleFromI18n } from '@/lib/date-utils';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import {
  Campaign,
  DatasourceInstructionMode,
} from '@/lib/graphql/generated/graphql';
import { useAddPreviousCampaignToDeal } from '@/lib/hooks/useAddPreviousCampaignToDeal';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import Button from '@appchoose/button';
import { format } from 'date-fns';
import { History } from 'lucide-react';

import { useIsOfferLocked } from '../hooks/use-is-offer-locked';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';

interface FarmingBrandEmptyStateProps {
  dealId: string;
  campaigns: Campaign[];
  onImportStart?: () => void;
}

export function FarmingBrandEmptyState({
  dealId,
  campaigns,
  onImportStart,
}: FarmingBrandEmptyStateProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  const isOfferLocked = useIsOfferLocked();

  const campaign = campaigns[0];

  const {
    addPreviousCampaignToDeal,
    loading: loadingHttpMutation,
    error,
    domainError,
    isDomainError,
  } = useAddPreviousCampaignToDeal();
  const progressToastTrackerIdRef = useRef<string | null>(null);

  const clearProgressToast = () => {
    if (!progressToastTrackerIdRef.current) return;
    clearDealItemsInProgressToast({
      trackerId: progressToastTrackerIdRef.current,
    });
    progressToastTrackerIdRef.current = null;
  };

  useErrorToastHandler({
    domainError,
    isDomainError,
    error: error ?? null,
    domainToast: {
      titleKey: 'general.error_import',
      defaultMessageKey: 'general.error_import_previous_sales_description',
      clearProgress: clearProgressToast,
    },
    genericToast: {
      titleKey: 'general.error_import',
      getDescription: () =>
        t('general.error_import_previous_sales_description'),
    },
  });

  const handleImport = async () => {
    try {
      setLoading(true);
      onImportStart?.();

      const { update, dismiss } = toast({
        description: `⏳ ${t('general.copy_previous_sales_in_progress')}`,
        duration: Number.POSITIVE_INFINITY,
      });
      const toastTrackerId = registerDealItemsInProgressToast(
        update as ToastUpdate,
        dismiss
      );
      progressToastTrackerIdRef.current = toastTrackerId;

      const result = await addPreviousCampaignToDeal(
        dealId,
        campaign.saleId,
        DatasourceInstructionMode.CreateOnly
      );

      if (result?.__typename === 'MutationSuccess') {
        bindDealItemsInProgressToastToDatasource(toastTrackerId, result.id);
        progressToastTrackerIdRef.current = null;
      } else {
        setLoading(false);
        clearProgressToast();
      }
    } catch {
      setLoading(false);
      clearProgressToast();
    }
  };

  const formattedDate = format(new Date(campaign.startAt), 'd MMMM yyyy', {
    locale: getDateFnsLocaleFromI18n(i18n.language),
  });

  const isLoading = loadingHttpMutation || loading;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
        <History className="h-8 w-8 text-gray-400" />
      </div>

      <div className={cn('flex flex-col gap-2', isOfferLocked && 'opacity-50')}>
        <h3 className="text-base font-semibold text-gray-900">
          {t('components.farming_brand_empty_state.title')}
        </h3>
        <p className="max-w-sm text-sm text-gray-500">
          {t('components.farming_brand_empty_state.description', {
            campaignName: campaign.name,
            date: formattedDate,
            count: campaign.numberOfProducts,
          })}
        </p>
      </div>

      <Button
        type="button"
        appearance="primary"
        onClick={handleImport}
        disabled={isLoading || isOfferLocked}
      >
        {isLoading
          ? t('components.import_flow.copy_previous_sales.importing')
          : t('components.farming_brand_empty_state.cta', {
              campaignName: campaign.name,
            })}
      </Button>
    </div>
  );
}
